import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Node as PMNode } from 'prosemirror-model';
import { EditorView, NodeView } from 'prosemirror-view';
import { InlineEntity } from '../../EntityHover/InlineEntity';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('INLINE_ENTITY', { enabled: false });

/**
 * ProseMirror NodeView that mounts a React `<InlineEntity>` chip inside a
 * `<span>` and keeps it in sync with the PM node's attributes.
 *
 * Design notes:
 * - `contentDOM` is undefined (atom node — no editable content inside).
 * - `stopEvent: true` on clicks so PM doesn't redirect the click to selection
 *   handling; the chip should behave like a link.
 * - `update()` re-renders when attrs change (e.g. label update after fetch).
 * - `destroy()` unmounts the React root.
 */
export class EntityMentionNodeView implements NodeView {
  dom: HTMLElement;
  node: PMNode;
  view: EditorView;
  getPos: () => number | undefined;
  private root: Root;

  constructor(node: PMNode, view: EditorView, getPos: () => number | undefined) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;

    this.dom = document.createElement('span');
    this.dom.className = 'entity-mention-chip-mount';
    this.dom.setAttribute('data-entity-mention', 'true');
    this.dom.setAttribute('data-entity-id', node.attrs.entityId || '');
    if (node.attrs.entityType) {
      this.dom.setAttribute('data-entity-type', node.attrs.entityType);
    }
    this.dom.setAttribute('contenteditable', 'false');

    log.log('[INLINE_ENTITY] EntityMentionNodeView mount', {
      entityId: node.attrs.entityId,
      label: node.attrs.label,
    });

    this.root = createRoot(this.dom);
    this.renderChip();
  }

  private renderChip(): void {
    this.root.render(
      <InlineEntity
        entityId={this.node.attrs.entityId}
        entityType={this.node.attrs.entityType ?? undefined}
      >
        {this.node.attrs.label || null}
      </InlineEntity>,
    );
  }

  update(node: PMNode): boolean {
    if (node.type !== this.node.type) return false;
    const attrsChanged =
      node.attrs.entityId !== this.node.attrs.entityId ||
      node.attrs.entityType !== this.node.attrs.entityType ||
      node.attrs.label !== this.node.attrs.label;
    this.node = node;
    if (attrsChanged) {
      this.dom.setAttribute('data-entity-id', node.attrs.entityId || '');
      if (node.attrs.entityType) {
        this.dom.setAttribute('data-entity-type', node.attrs.entityType);
      } else {
        this.dom.removeAttribute('data-entity-type');
      }
      log.log('[INLINE_ENTITY] EntityMentionNodeView update', {
        entityId: node.attrs.entityId,
        label: node.attrs.label,
      });
      this.renderChip();
    }
    return true;
  }

  stopEvent(event: Event): boolean {
    // Allow the chip's own click handlers to fire.
    if (event.type === 'mousedown' || event.type === 'click') return true;
    return false;
  }

  ignoreMutation(): boolean {
    // React owns the subtree — don't let PM re-parse it.
    return true;
  }

  destroy(): void {
    log.log('[INLINE_ENTITY] EntityMentionNodeView destroy', {
      entityId: this.node.attrs.entityId,
    });
    // Defer unmount to avoid "synchronous unmount during render" warnings when
    // PM tears down the view during a transaction commit.
    const root = this.root;
    queueMicrotask(() => {
      try { root.unmount(); } catch { /* noop */ }
    });
  }
}
